import React from 'react';
import { styled } from '@mui/material';
import { Colors } from 'library';
import { InboxOutlined, ArrowUpward, ArrowDownward, SwapVert } from '@mui/icons-material';
import { EmptyText } from './EmptyText';
import { TablePagination } from './TablePagination';

export type TableSortDirection = 'asc' | 'desc';

export interface TableColumn<T> {
    key: string;
    label: string;
    sortable?: boolean;
    sortKey?: string;
    render?: (row: T) => React.ReactNode;
    width?: string | number;
}

interface TableProps<T> {
    columns: TableColumn<T>[];
    data: T[];
    emptyMessage?: string;
    loadingRowCount?: number;
    onRowClick?: (row: T) => void;
    page?: number;
    count?: number;
    perPage?: number;
    onPageChange?: (page: number) => void;
    sortBy?: string;
    sortDirection?: TableSortDirection;
    onSortChange?: (sortBy: string, direction: TableSortDirection) => void;
}

export function Table<T extends { id: string | number }>({
    columns,
    data,
    emptyMessage = 'No results found',
    loadingRowCount = 0,
    onRowClick,
    page,
    count,
    perPage,
    onPageChange,
    sortBy,
    sortDirection,
    onSortChange,
}: TableProps<T>) {
    const hasPagination =
        page !== undefined && count !== undefined && perPage !== undefined && !!onPageChange && count > perPage;

    function handleSort(col: TableColumn<T>) {
        if (!col.sortable || !onSortChange) return;
        const key = col.sortKey ?? col.key;
        const newDirection: TableSortDirection = sortBy === key && sortDirection === 'asc' ? 'desc' : 'asc';
        onSortChange(key, newDirection);
    }

    function getSortIcon(col: TableColumn<T>) {
        const key = col.sortKey ?? col.key;
        if (sortBy === key) {
            return sortDirection === 'asc' ? (
                <ArrowUpward sx={{ fontSize: 14, color: Colors.textSecondary }} />
            ) : (
                <ArrowDownward sx={{ fontSize: 14, color: Colors.textSecondary }} />
            );
        }
        return <SwapVert sx={{ fontSize: 14, color: Colors.border }} />;
    }

    return (
        <TableContainer>
            <TableScroll>
                <StyledTable>
                    <thead>
                        <HeaderRow>
                            {columns.map((col) => (
                                <HeaderCell
                                    key={col.key}
                                    style={{ width: col.width }}
                                    clickable={!!col.sortable && !!onSortChange}
                                    onClick={() => handleSort(col)}
                                >
                                    <HeaderCellContent>
                                        {col.label}
                                        {col.sortable && getSortIcon(col)}
                                    </HeaderCellContent>
                                </HeaderCell>
                            ))}
                        </HeaderRow>
                    </thead>
                    <tbody>
                        {data.length === 0 && loadingRowCount > 0 ? (
                            Array.from({ length: loadingRowCount }, (_, rowIndex) => (
                                <DataRow key={`loading-${rowIndex}`} clickable={false} aria-hidden="true">
                                    {columns.map((col, i) => (
                                        <DataCell key={col.key} first={i === 0}>
                                            {i === 0 ? (
                                                <SkeletonRow>
                                                    <SkeletonCircle>
                                                        <SkeletonBar width="50%" thin />
                                                    </SkeletonCircle>
                                                    <SkeletonStack>
                                                        <SkeletonBar width="60%" />
                                                        <SkeletonBar width="80%" thin />
                                                        <SkeletonBar width="40%" thin />
                                                    </SkeletonStack>
                                                </SkeletonRow>
                                            ) : (
                                                <SkeletonPill>
                                                    <SkeletonDot />
                                                    <SkeletonStack>
                                                        <SkeletonBar width="70%" />
                                                        <SkeletonBar width="45%" thin />
                                                    </SkeletonStack>
                                                </SkeletonPill>
                                            )}
                                        </DataCell>
                                    ))}
                                </DataRow>
                            ))
                        ) : data.length === 0 ? (
                            <tr>
                                <EmptyCell colSpan={columns.length}>
                                    <EmptyState>
                                        <InboxOutlined sx={{ fontSize: 40, color: Colors.border }} />
                                        <EmptyText padding="none">{emptyMessage}</EmptyText>
                                    </EmptyState>
                                </EmptyCell>
                            </tr>
                        ) : (
                            data.map((row) => (
                                <DataRow key={row.id} clickable={!!onRowClick}>
                                    {columns.map((col, i) => (
                                        <DataCell
                                            key={col.key}
                                            first={i === 0}
                                            clickable={i === 0 && !!onRowClick}
                                            onClick={i === 0 && onRowClick ? () => onRowClick(row) : undefined}
                                        >
                                            {i === 0 && onRowClick ? (
                                                <RowButton type="button">
                                                    {col.render
                                                        ? col.render(row)
                                                        : String((row as Record<string, unknown>)[col.key] ?? '')}
                                                </RowButton>
                                            ) : col.render ? (
                                                col.render(row)
                                            ) : (
                                                String((row as Record<string, unknown>)[col.key] ?? '')
                                            )}
                                        </DataCell>
                                    ))}
                                </DataRow>
                            ))
                        )}
                    </tbody>
                </StyledTable>
            </TableScroll>
            {hasPagination && (
                <PaginationWrapper>
                    <TablePagination count={count} perPage={perPage} page={page} onPageChange={onPageChange} />
                </PaginationWrapper>
            )}
        </TableContainer>
    );
}

const TableContainer = styled('div')({
    border: `1px solid ${Colors.border}`,
    borderRadius: 10,
    overflow: 'visible',
    backgroundColor: Colors.white,
});

const TableScroll = styled('div')({
    overflowX: 'auto',
    borderRadius: 10,
});

const StyledTable = styled('table')({
    width: '100%',
    borderCollapse: 'collapse',
});

const HeaderRow = styled('tr')({
    backgroundColor: Colors.fill,
});

const HeaderCell = styled('th')<{ clickable?: boolean }>(({ clickable }) => ({
    padding: '14px 20px',
    textAlign: 'left',
    fontSize: 13,
    fontWeight: 400,
    color: Colors.textSecondary,
    borderBottom: `1px solid ${Colors.border}`,
    whiteSpace: 'nowrap',
    cursor: clickable ? 'pointer' : 'default',
    userSelect: clickable ? 'none' : 'auto',
    '&:hover': clickable ? { color: Colors.primary } : {},
    '&:first-child': {
        borderTopLeftRadius: 10,
    },
    '&:last-child': {
        borderTopRightRadius: 10,
    },
}));

const HeaderCellContent = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
});

const DataRow = styled('tr')<{ clickable: boolean }>(({ clickable }) => ({
    borderBottom: `1px solid ${Colors.border}`,
    '&:hover': {
        backgroundColor: clickable ? Colors.surface : 'transparent',
    },
    '&:last-child': {
        borderBottom: 'none',
    },
}));

const DataCell = styled('td')<{ first?: boolean; clickable?: boolean }>(({ first, clickable }) => ({
    padding: '16px 20px',
    fontSize: 14,
    fontWeight: first ? 500 : 400,
    color: Colors.primary,
    cursor: clickable ? 'pointer' : 'default',
    '&:first-child': {
        borderBottomLeftRadius: 10,
    },
    '&:last-child': {
        borderBottomRightRadius: 10,
    },
}));

const RowButton = styled('button')({
    all: 'unset',
    cursor: 'pointer',
    outline: 'none',
    '&:focus, &:focus-visible': {
        outline: 'none',
        boxShadow: 'none',
    },
});

const skeletonPulse = {
    backgroundColor: Colors.fill,
    animation: 'tableSkeletonPulse 1.2s ease-in-out infinite',
    '@keyframes tableSkeletonPulse': {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.5 },
    },
};

const SkeletonRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
});

const SkeletonStack = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    flex: 1,
});

const SkeletonCircle = styled('div')({
    ...skeletonPulse,
    width: 36,
    height: 36,
    borderRadius: '50%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const SkeletonPill = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    maxWidth: 180,
});

const SkeletonDot = styled('div')({
    ...skeletonPulse,
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
});

const SkeletonBar = styled('div')<{ width: string; thin?: boolean }>(({ width, thin }) => ({
    ...skeletonPulse,
    width,
    height: thin ? 10 : 14,
    borderRadius: 6,
}));

const EmptyCell = styled('td')({
    padding: 0,
});

const EmptyState = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    gap: 12,
});

const PaginationWrapper = styled('div')({
    borderTop: `1px solid ${Colors.border}`,
});
